import { ref, computed } from 'vue'
import { getActivePinia } from 'pinia'
import { useAuthStore } from '../stores/auth'
import { useGameStore } from '../stores/game'
import { useThemeStore } from '../stores/theme'
import { getTheme, type Theme, type ThemeColors, type ThemeMode } from '../config/themes'
import { normalizeThemeModeColors } from '../utils/themeModeNormalize'
import { resolveThemeName, loadThemeWithFallback } from '../utils/themeLoader'
import { setThemeGameFlags } from '../utils/resultParser'

/** Root-level assets under public/img that must not be rewritten to /themes/{name}/img/... */
const SHARED_ROOT_IMG_PATHS = new Set([
  '/img/cancelled.png',
  '/img/cancelled.svg',
])

function isSharedRootAsset(path: string): boolean {
  return SHARED_ROOT_IMG_PATHS.has(path)
}

const currentThemeName = ref<string>('default')
const currentTheme = ref<Theme>(getTheme('default'))
let isInitializing = false // Prevent concurrent initialization
let lastInitializedAccount: string | null = null // Track last account theme was initialized for
let appliedThemeColorKeys = new Set<string>()

/** Theme from current page URL (search or hash). Use this so URL theme wins over game-settings. */
export function getThemeParamFromUrl(): string | null {
  try {
    const search = new URLSearchParams(window.location.search)
    const fromSearch = search.get('theme')
    if (fromSearch) return fromSearch.trim() || null
    const hash = window.location.hash
    const hashIdx = hash.indexOf('?')
    if (hashIdx !== -1) {
      const fromHash = new URLSearchParams(hash.slice(hashIdx)).get('theme')
      if (fromHash) return fromHash.trim() || null
    }
  } catch {
    // ignore
  }
  return null
}

function getCurrentLocale(): string {
  try {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('locale') || 'en'
  } catch {
    return 'en'
  }
}

function getActiveThemeMode(): ThemeMode {
  return document.documentElement.classList.contains('theme-dark') ? 'dark' : 'light'
}

/** True when `currentTheme` came from a JSON file (modes + colors), not the bundled fallback. */
function isFileBackedTheme(theme: Theme): boolean {
  return Boolean(theme.modes && theme.colors)
}

function resolveThemeColors(theme: Theme, mode: ThemeMode): ThemeColors {
  const resolved = normalizeThemeModeColors({
    ...(theme.colors || {}),
    ...(theme.modes?.[mode] || {}),
  })
  if (!resolved || Object.keys(resolved).length === 0) {
    return getTheme('default').colors as ThemeColors
  }
  return resolved as ThemeColors
}

function toCssVarName(key: string): string {
  return `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`
}

function getThemeColorKeys(theme: Theme): string[] {
  const light = normalizeThemeModeColors({
    ...(theme.colors || {}),
    ...(theme.modes?.light || {}),
  })
  const dark = normalizeThemeModeColors({
    ...(theme.colors || {}),
    ...(theme.modes?.dark || {}),
  })
  return Array.from(new Set([...Object.keys(light), ...Object.keys(dark)]))
}

const THEME_GOOGLE_FONT_LINK_ID = 'theme-google-fonts'
const THEME_OVERRIDES_LINK_ID = 'theme-overrides-css'

function applyThemeGoogleFonts(typography: Theme['typography']): void {
  const url = typography.googleFontsUrl
  let link = document.getElementById(THEME_GOOGLE_FONT_LINK_ID) as HTMLLinkElement | null
  if (url) {
    if (!link) {
      link = document.createElement('link')
      link.id = THEME_GOOGLE_FONT_LINK_ID
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    }
    link.href = url
    return
  }
  link?.remove()
}

function applyThemeOverrides(themeName: string): void {
  let link = document.getElementById(THEME_OVERRIDES_LINK_ID) as HTMLLinkElement | null
  const themeOverridesHref =
    themeName === 'livescorebet' || themeName.startsWith('livescorebet-')
      ? `/themes/${themeName}/css/overrides.css`
      : themeName === 'wsb'
        ? '/themes/wsb/css/overrides.css'
        : themeName === 'ebetcoza'
          ? '/themes/ebetcoza/css/overrides.css'
          : null

  if (themeOverridesHref) {
    if (!link) {
      link = document.createElement('link')
      link.id = THEME_OVERRIDES_LINK_ID
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    }
    link.href = themeOverridesHref
    return
  }
  link?.remove()
}

function applyTheme(theme: Theme, mode: ThemeMode = getActiveThemeMode()): void {
  const root = document.documentElement
  const resolvedColors = resolveThemeColors(theme, mode)

  // Clear previous theme tokens too, including optional widget colors and icons.
  // Otherwise theme-specific values can survive switching themes without a reload.
  const nextColorKeys = new Set(getThemeColorKeys(theme))
  new Set([...appliedThemeColorKeys, ...nextColorKeys]).forEach((key) => {
    const cssVar = toCssVarName(key)
    root.style.removeProperty(cssVar)
    document.body.style.removeProperty(cssVar)
  })
  appliedThemeColorKeys = nextColorKeys
  
  Object.entries(resolvedColors).forEach(([key, value]) => {
    if (!value) return
    const cssVar = toCssVarName(key)
    root.style.setProperty(cssVar, value)
    document.body.style.setProperty(cssVar, value)
  })
  
  Object.entries(theme.typography).forEach(([key, value]) => {
    if (key === 'googleFontsUrl' || value == null) return
    const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase()
    root.style.setProperty(`--typography-${cssKey}`, String(value))
  })
  applyThemeGoogleFonts(theme.typography)
  applyThemeOverrides(theme.name)
  
  Object.entries(theme.spacing).forEach(([key, value]) => {
    root.style.setProperty(`--spacing-${key}`, value)
  })
  
  Object.entries(theme.borderRadius).forEach(([key, value]) => {
    root.style.setProperty(`--radius-${key}`, value)
  })
  
  Object.entries(theme.shadows).forEach(([key, value]) => {
    root.style.setProperty(`--shadow-${key}`, value)
  })
  
  root.setAttribute('data-account-theme', theme.name)

  setThemeGameFlags(
    theme.assets?.gameFlagsBySlug,
    theme.assets?.gameFlagsBySlugPrefix
  )

  if (theme.interaction?.flat) {
    root.setAttribute('data-theme-interactions', 'flat')
  } else {
    root.removeAttribute('data-theme-interactions')
  }
}

/** Apply theme navigation rules (e.g. dark-only themes with no toggle). */
export function applyThemeNavigationPolicy(theme: Theme): void {
  if (!getActivePinia()) return
  const themeStore = useThemeStore()
  const forced = theme.navigation?.forceColorMode
  if (forced === 'light' || forced === 'dark') {
    themeStore.setLockedColorMode(forced)
    themeStore.setMode(forced)
    return
  }
  themeStore.setLockedColorMode(null)
}

/** Apply the active account palette for the current light/dark mode. */
export function syncAccountThemeCssVariables(): void {
  applyTheme(currentTheme.value, getActiveThemeMode())
}

export async function initializeTheme(force = false): Promise<void> {
  if (isInitializing && !force) {
    while (isInitializing) {
      await new Promise(resolve => setTimeout(resolve, 50))
    }
    return
  }

  try {
    isInitializing = true

    const urlTheme = getThemeParamFromUrl() || undefined
    const authStore = useAuthStore()
    const account = authStore.account || sessionStorage.getItem('auth_account')

    if (!force && lastInitializedAccount === account && currentThemeName.value !== 'default') {
      if (!urlTheme || resolveThemeName(urlTheme) === currentThemeName.value) {
        isInitializing = false
        return
      }
    }

    // 1. URL `theme` wins. 2. Else theme from game settings (backend). 3. Else `default`.
    let themeName: string | null = null
    if (!urlTheme) {
      try {
        const gameStore = useGameStore()
        if (gameStore.getBetSettings?.theme) {
          themeName = gameStore.getBetSettings.theme
        } else if (account) {
          try {
            await gameStore.fetchGameSettings(false)
            themeName = gameStore.getBetSettings?.theme || null
          } catch (e) {
            console.warn('Failed to fetch game settings for theme:', e)
          }
        }
      } catch (e) {
        console.warn('Error accessing game store for theme:', e)
      }
    }

    const resolvedThemeName = urlTheme ? resolveThemeName(urlTheme) : resolveThemeName(themeName ?? undefined)

    if (
      !force &&
      resolvedThemeName === currentThemeName.value &&
      lastInitializedAccount === account &&
      isFileBackedTheme(currentTheme.value)
    ) {
      isInitializing = false
      return
    }

    const theme = await loadThemeWithFallback(resolvedThemeName)
    await setTheme(theme.name, true)
    currentTheme.value = theme
    lastInitializedAccount = account
    applyThemeNavigationPolicy(theme)
  } catch (error) {
    console.error('Failed to initialize theme:', error)
    const defaultTheme = getTheme('default')
    await setTheme('default', false)
    currentTheme.value = defaultTheme
  } finally {
    isInitializing = false
  }
}

export async function setTheme(themeName: string, loadFromFile = true): Promise<void> {
  try {
    const theme = loadFromFile
      ? await loadThemeWithFallback(themeName)
      : getTheme(themeName) || getTheme('default')
    
    currentThemeName.value = themeName
    currentTheme.value = theme
    applyTheme(theme, getActiveThemeMode())
    applyThemeNavigationPolicy(theme)
  } catch (error) {
    console.error(`Failed to set theme ${themeName}:`, error)
    const defaultTheme = getTheme('default')
    currentThemeName.value = 'default'
    currentTheme.value = defaultTheme
    applyTheme(defaultTheme, getActiveThemeMode())
  }
}

export function useTheme() {
  const getAssetPath = (path: keyof Theme['assets'] | string, ...args: unknown[]): string => {
    const assets = currentTheme.value.assets
    const themeName = currentThemeName.value
    
    if (typeof path !== 'string' || !(path in assets)) {
      return ''
    }
    
    const asset = assets[path as keyof Theme['assets']]
    
    if (typeof asset === 'function') {
      return (asset as (...args: unknown[]) => string)(...args)
    }
    
    if (typeof asset === 'string') {
      let resolvedPath = asset
      const locale = args[0] && typeof args[0] === 'string' ? args[0] : getCurrentLocale()
      resolvedPath = resolvedPath.replace('{locale}', locale)
      resolvedPath = resolvedPath.replace('/locale/en/', `/locale/${locale}/`)
      
      if (resolvedPath.includes('/themes/')) {
        // Replace any theme name in the path with the current theme name
        resolvedPath = resolvedPath.replace(/\/themes\/[^/]+/, `/themes/${themeName}`)
        return resolvedPath
      }

      if (isSharedRootAsset(resolvedPath)) {
        return resolvedPath
      }
      
      if (themeName !== 'default' && resolvedPath.startsWith('/img/')) {
        return resolvedPath.replace('/img/', `/themes/${themeName}/img/`)
      }
      
      return resolvedPath
    }
    
    if (typeof asset === 'object' && asset !== null) {
      const assetRecord = asset as Record<string, unknown>
      const requestedKey = typeof args[0] === 'string' ? args[0] : ''
      // heroBanner / heroBannerMobile use { light, dark }; footer/howToPlay use named keys.
      const hasModeVariants =
        typeof assetRecord.light === 'string' || typeof assetRecord.dark === 'string'
      const nestedKey =
        requestedKey && requestedKey in assetRecord
          ? requestedKey
          : hasModeVariants
            ? getActiveThemeMode()
            : requestedKey
      const nestedAsset = assetRecord[nestedKey]
      // Locale is args[0] for string assets; for mode objects with no explicit key it's args[0]
      // only when that arg is a locale, otherwise args[1] after an explicit nested key.
      const localeArgIndex =
        requestedKey && requestedKey in assetRecord ? 1 : hasModeVariants ? 0 : 1
      
      if (!nestedAsset) return ''
      
      if (typeof nestedAsset === 'function') {
        const result = (nestedAsset as (...args: unknown[]) => string)(...args.slice(1))
        if (result.includes('/themes/')) {
          // Replace any theme name in the path with the current theme name
          return result.replace(/\/themes\/[^/]+/, `/themes/${themeName}`)
        }
        if (isSharedRootAsset(result)) {
          return result
        }
        if (result.startsWith('/img/') && themeName !== 'default') {
          return result.replace('/img/', `/themes/${themeName}/img/`)
        }
        return result
      }
      
      if (typeof nestedAsset === 'string') {
        let resolvedPath = nestedAsset
        const localeArg = args[localeArgIndex]
        const locale = typeof localeArg === 'string' && !['light', 'dark'].includes(localeArg)
          ? localeArg
          : getCurrentLocale()
        resolvedPath = resolvedPath.replace('{locale}', locale)
        resolvedPath = resolvedPath.replace('/locale/en/', `/locale/${locale}/`)
        
        if (resolvedPath.includes('/themes/')) {
          // Replace any theme name in the path with the current theme name
          resolvedPath = resolvedPath.replace(/\/themes\/[^/]+/, `/themes/${themeName}`)
          return resolvedPath
        }
        if (isSharedRootAsset(resolvedPath)) {
          return resolvedPath
        }
        if (resolvedPath.startsWith('/img/') && themeName !== 'default') {
          return resolvedPath.replace('/img/', `/themes/${themeName}/img/`)
        }
        return resolvedPath
      }
      
      return ''
    }
    
    return ''
  }

  return {
    currentTheme: computed(() => currentTheme.value),
    currentThemeName: computed(() => currentThemeName.value),
    setTheme,
    applyTheme,
    getAssetPath,
  }
}
